import { type TransitionState } from '@atlassian/state-machine/types';
import { type UFOExperience } from '@atlassian/ufo';
import type AnalyticsWebClient from '@atlassiansox/analytics-web-client';
import EventEmitter from 'eventemitter3';

// todo: https://product-fabric.atlassian.net/browse/CNS-10273 - Remove dependency
// eslint-disable-next-line no-restricted-imports
import { currentSessionId } from '@canvas/canvas-state/analytics';
import { type CollabInfrastructure } from '@canvas/document-id';
import { MetricName, ReliabilityMetrics, type Timing } from '@canvas/frontend-metrics';

import { type CollabStrategy } from '../collab-strategy';
import {
  createNetworkStateMachine,
  type SocketStateMachine,
  type State,
} from '../state-machine/network-state-machine';
import {
  type PendingMessage,
  type SocketMachineContext,
} from '../state-machine/network-state-machine-context';
import { type Events } from '../state-machine/network-state-machine-events';
import { untilInState } from '../state-machine/state-machine-utils';
import { activeStates } from '../state-machine/states/active/active-state';

import { type RemoteDocument } from './document';
import { Queue } from './queue';
import { SocketReconnectDebounce, type Waiter } from './socket-reconnect-debounce';
import { type StateBridge } from './state-bridge';
import { WebSocketCodes } from './websocket-codes';

export interface YjsSocketEvents {
  message: (buffer: Uint8Array<ArrayBuffer>, sender?: string) => void;
  transientMessage: (buffer: Uint8Array<ArrayBuffer>) => void;
  connected: () => void;
  disconnected: () => void;
  failingRetry: (error: Error) => void;
  connecting: (attempt: number) => void;
}

export interface RelaySupplierResult {
  /**
   * The URL to connect to.
   */
  url: string;
  /**
   * The socket.io namespace to connect to.
   */
  namespace?: string;
  /**
   * Extra headers to pass to the transport (e.g. manually setting cookies in collab-sim).
   */
  extraHeaders?: Record<string, string>;
}

export interface AuthenticatedSocketOptions {
  accessTokenSupplier: () => Promise<string>;
  relaySupplier: (
    attempt: number,
    signal: AbortSignal,
    collabStrategy: CollabStrategy
  ) => RelaySupplierResult;
  onSocketFetchSnapshot: (signal: AbortSignal) => Promise<RemoteDocument>;
  onSocketFetchSteps: (
    remoteDocument: RemoteDocument,
    attempt: number,
    signal: AbortSignal
  ) => Promise<RemoteDocument>;
  onSocketApplyChanges: (relayUrl: string, remoteDoc: RemoteDocument) => RemoteDocument;
  onSocketUpdateChanges?: (
    remoteDocument: RemoteDocument,
    connectionAttempts: number,
    signal: AbortSignal
  ) => void;
  reconcileOfflineChanges?: (remoteDocument: RemoteDocument) => Promise<void>;
  /**
   * Triggered when the socket is closed. Reconnects may still take place after
   * it has closed. Used this hook to execute logic after a socket close event.
   */
  onSocketClosed?: (reason: WebSocketCodes) => void;
  onAckTimeout?(): void;
  debounceWaiter?: Waiter;
  shouldHaltRetry: () => boolean;
  socketFactory?: SocketFactory;
  getAnalyticsWebClient?: () => Promise<AnalyticsWebClient>;
  stateBridge?: StateBridge;
  signal?: AbortSignal;
  /**
   * Whether the state machine should skip fetching steps and applying changes.
   * This is useful in cases like version history where we don't want to apply
   * the latest changes.
   */
  shouldSkipUpdates: () => boolean;
}

export interface SocketInterface extends EventEmitter<YjsSocketEvents> {
  send(data: Uint8Array<ArrayBufferLike>, enqueue?: boolean): void;
  sendToPeer(data: Uint8Array<ArrayBufferLike>, peer: string): void;
  sendAwareness(data: Uint8Array<ArrayBufferLike>): void;
  clearPendingMessages(): void;
  destroy(): void;
  connect(): Promise<void>;
  pause(): void;
  resume(): void;
  restart(): void;
  flushTransport(ignoreAck?: boolean): void;
  open: boolean;
  state: State;
  collabInfrastructure: CollabInfrastructure | undefined;
}

export type SocketFactory = (url: string) => Promise<WebSocket> | WebSocket;

type AuthenticatedSocketTiming = {
  key: State;
  startMark: `start:${State}`;
  endMark: `end:${State}`;
} & Timing;

const performanceTimings: AuthenticatedSocketTiming[] = activeStates.map((state) => ({
  key: state,
  startMark: `start:${state}`,
  endMark: `end:${state}`,
}));

export class AuthenticatedSocket
  extends EventEmitter<YjsSocketEvents>
  implements SocketInterface
{
  private stateMachine: SocketStateMachine;
  private controller: AbortController = new AbortController();
  private messages: Queue<PendingMessage> = new Queue<PendingMessage>();
  private readonly onSocketClosed: (reason: WebSocketCodes) => void;
  private metric: UFOExperience | undefined;

  constructor({
    accessTokenSupplier,
    relaySupplier,
    onSocketFetchSnapshot,
    onSocketFetchSteps,
    onSocketApplyChanges,
    onSocketUpdateChanges,
    debounceWaiter,
    shouldHaltRetry,
    signal,
    onSocketClosed,
    shouldSkipUpdates,
    reconcileOfflineChanges,
    getAnalyticsWebClient,
    stateBridge,
    onAckTimeout,
  }: AuthenticatedSocketOptions) {
    super();
    this.onSocketClosed = onSocketClosed ?? (() => {});

    this.stateMachine = createNetworkStateMachine({
      sessionId: currentSessionId.sessionId,
      getAnalyticsWebClient,
      stateBridge,
      socketFetchSnapshot: onSocketFetchSnapshot,
      socketFetchSteps: onSocketFetchSteps,
      socketApplyChanges: onSocketApplyChanges,
      socketUpdateChanges: onSocketUpdateChanges ?? (() => {}),
      socketConnect: this.onSocketConnect,
      onAckTimeout,
      accessTokenSupplier,
      relaySupplier: relaySupplier,
      debounce: new SocketReconnectDebounce(
        (error) => this.emit('failingRetry', error),
        debounceWaiter
      ),
      signal: this.controller.signal,
      shouldHaltRetry,
      pendingMessages: this.messages,
      shouldSkipUpdates,
      reconcileOfflineChanges,
    });

    this.stateMachine.onEvent(this.onStateMachineEvent);
    this.stateMachine.onTransition(this.onStateMachineTransition);
    signal?.addEventListener('abort', this.destroy, {
      signal: this.controller.signal,
      once: true,
    });
  }

  private startPerformanceTiming = (state: State) => {
    this.metric?.mark(`start:${state}`);
  };

  private endPerformanceTiming = (state: State) => {
    this.metric?.mark(`end:${state}`);
  };

  private successConnectionMetric = () => {
    this.metric?.success();
  };

  private failureConnectionMetric = () => {
    this.metric?.failure();
  };

  // authentication/token errors, network connectivity errors, connection timeout errors should abort
  private shouldAbortForClientError = (ev: Events): boolean => {
    if (ev.type !== 'error' || !ev.error?.message) {
      return false;
    }

    const errorMessage = ev.error.message.toLowerCase();

    if (errorMessage.includes('could not fetch token after multiple attempts')) {
      return true;
    }

    if (
      errorMessage.includes('failed to fetch') ||
      errorMessage.includes('networkerror when attempting to fetch resource')
    ) {
      return true;
    }

    if (errorMessage.includes('connection timeout')) {
      return true;
    }

    return false;
  };

  private onSocketConnect = () => {
    // This needs to happen here and not onStateMachineEvent or onStateMachineTransition,
    // since we have additional logic to determine if the WebSocket has actually
    // connected successfully in the state machine.
    //
    // The state machine will call this method when it has determined that the WebSocket
    // is ready to send messages.
    const event = 'connected';
    this.endPerformanceTiming(event);
    this.successConnectionMetric();
    this.emit(event);
  };

  private onStateMachineEvent = (ev: Events) => {
    switch (ev.type) {
      case 'messageReceived':
        this.emit('message', ev.message, ev.sender);
        break;
      case 'transientMessageReceived':
        this.emit('transientMessage', ev.message);
        break;
      case 'close':
        this.emit('disconnected');
        this.onSocketClosed(ev.code);
        break;
      case 'debounceComplete':
        this.emit('connecting', this.stateMachine.context().debounce.attempt);
    }
  };

  private onStateMachineTransition = (
    ev: Events,
    ctx: SocketMachineContext,
    { current, previous }: TransitionState<State>
  ) => {
    if (current === previous) {
      return;
    }

    switch (current) {
      case 'connecting:error': {
        this.endPerformanceTiming(previous);

        const isSessionLimitError =
          (ev.type === 'error' && ev.error?.name === 'WSConnectionLimitExceededError') ||
          (ev.type === 'close' && ev.code === WebSocketCodes.SESSION_FULL);

        const isClientSideError = this.shouldAbortForClientError(ev);

        if (isSessionLimitError) {
          this.metric?.abort({ metadata: { reason: 'session_limit_reached' } });
        } else if (isClientSideError) {
          this.metric?.abort({
            metadata: {
              reason: 'client_side_error',
              errorMessage: ev.type === 'error' ? ev.error?.message : undefined,
              state: previous,
            },
          });
        } else {
          this.failureConnectionMetric();
        }
        this.stateMachine.context().debounce.captureErrorState(previous);
        break;
      }
    }

    this.endPerformanceTiming(previous);
    this.startPerformanceTiming(current);

    const details = {
      traceId: ctx.traceId,
      sessionId: ctx.sessionId,
      event: ev.type,
      ...(ev.type === 'error' || ev.type === 'rejected'
        ? {
            error: ev.error,
          }
        : {}),
    };
    console.debug(`[collab] socket transition from ${previous} to ${current}`, details);
  };

  public async connect() {
    if (this.controller.signal.aborted) {
      // Don't attempt to connect if connection is aborted
      return;
    }

    if (this.stateMachine.state() === 'init') {
      this.metric = ReliabilityMetrics.getInstance().start(
        MetricName.WEBSOCKET_CONNECT,
        performanceTimings
      );
      this.startPerformanceTiming('init');
      this.stateMachine.send({
        type: 'start',
      });
    }

    const state = await untilInState(this.stateMachine, 'connected', 'init');
    const context = this.stateMachine.context();

    if (state === 'closed' && context.transport) {
      throw context.error ?? new Error('Encountered error creating socket');
    }
  }

  public get open() {
    return this.stateMachine.state() === 'connected';
  }

  public send(message: Uint8Array<ArrayBuffer>, enqueue = false) {
    this.messages.enqueue({
      message,
      enqueue,
    });
    this.stateMachine.send({
      type: 'send',
    });
  }

  public sendToPeer(message: Uint8Array<ArrayBuffer>, peer: string) {
    const ctx = this.stateMachine.context();
    if (ctx.transport?.sendToPeer) {
      return ctx.transport.sendToPeer(message, peer);
    }
    throw new Error('sendToPeer called in CF mode');
  }

  public sendAwareness(message: Uint8Array<ArrayBuffer>) {
    const ctx = this.stateMachine.context();
    // remote document is always set at this point
    // we still do a .? for defensive programming
    ctx.remoteDocument?.collabStrategy.sendAwareness(message, ctx.transport, (msg) =>
      this.send(msg)
    );
  }

  /**
   * Clear all pending messages in the queue without sending them.
   */
  public clearPendingMessages() {
    this.messages.dequeueAll();
  }

  /**
   * Temporarily disconnect the socket and wait until resume is called
   */
  public pause = () => {
    this.stateMachine.send({
      type: 'pause',
    });
  };

  public resume = () => {
    this.stateMachine.send({
      type: 'resume',
    });
  };

  /**
   * Permanently socket and cleanup listeners
   */
  public destroy = () => {
    this.controller.abort();
    this.emit('disconnected');
  };

  public restart = () => {
    this.stateMachine.send({
      type: 'restart',
    });
  };

  get state() {
    return this.stateMachine.state();
  }

  get collabInfrastructure() {
    return this.stateMachine.context().remoteDocument?.collabStrategy.type;
  }

  public flushTransport(ignoreAck?: boolean) {
    this.stateMachine.context().transport?.flush(ignoreAck);
  }
}
